import { Commons } from "./module/Commons.js";

declare const bootstrap: any;
declare const Shepherd: any;

interface StepConfig {
  id: string;
  text: string;
  focusClass: string;
  position: "top" | "bottom" | "left" | "right";
  isFirst?: boolean;
  isLast?: boolean;
}

// --- メインエントリーポイント ---
window.addEventListener("DOMContentLoaded", async () => {
  Commons.scrollToTop();
  Commons.scrollToName();

  initNavbar();
  initMobileMenu();
  initInfoBox();
  initFormValidation();
  initShepherdTour();
});

// --- 1. ナビゲーション背景制御 ---
function initNavbar(): void {
  const navbar = document.querySelector<HTMLElement>(".js-navbar");
  if (!navbar) return;

  window.addEventListener("scroll", () => {
    navbar.classList.toggle("is-scrolled", window.scrollY > 10);
  });
}

// --- 2. スマホメニューの自動クローズ ---
function initMobileMenu(): void {
  const links = document.querySelectorAll<HTMLElement>('.navbar-collapse .nav-link');
  
  links.forEach(link => {
    link.addEventListener('click', () => {
      const navbar = document.querySelector<HTMLElement>('.navbar-collapse');
      if (!navbar) return;

      const bsCollapse = bootstrap.Collapse.getInstance(navbar)
        || new bootstrap.Collapse(navbar, { toggle: false });

      bsCollapse.hide();
    });
  });
}

// --- 3. お知らせボックス ---
function initInfoBox(): void {
  const dispAll = document.getElementById("dispAll");
  const infoBox = document.getElementById("infoBox");

  if (!(dispAll instanceof HTMLElement && infoBox instanceof HTMLElement)) return;

  const infoMap = {
    close: { height: "auto", overflow: "inherit", show: "open", text: "閉じる" },
    open:  { height: "100px", overflow: "hidden", show: "close", text: "すべて見る" },
  } as const;

  dispAll.addEventListener("click", () => {
    const currentShow = dispAll.dataset.show;
    const show = (currentShow === "open" || currentShow === "close") ? currentShow : "open";
    
    const config = infoMap[show];
    infoBox.style.height = config.height;
    infoBox.style.overflow = config.overflow;
    dispAll.dataset.show = config.show;
    dispAll.textContent = config.text;
  });
}

// --- 4. フォームバリデーション ---
function initFormValidation(): void {
  const forms = document.querySelectorAll<HTMLFormElement>(".needs-validation");
  
  forms.forEach((form) => {
    form.addEventListener("submit", (e: Event) => {
      if (!form.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();
      }
      form.classList.add("was-validated");
    }, false);
  });
}

// --- 5. Shepherd ツアー関連 ---
function initShepherdTour(): void {
  const startTour = document.getElementById("start-tour");
  if (!startTour) return;

  // ツアーのステップデータ定義
  // 1. まず focusClass 以外のデータを持った配列を定義
  const rawSteps = [
    { id: "1",   position: "bottom", isFirst: true, text: "パルス・サーベイを開始。いつものスマホで答えれます。" },
    { id: "2",   position: "bottom", text: "逆転項目なし、表現統一で迷いにくい。直感的に回答できます。" },
    { id: "2-1", position: "bottom", text: "逆転項目なし、表現統一で迷いにくい。直感的に回答できます。" },
    { id: "3",   position: "top",    text: "逆転項目なし、表現統一で迷いにくい。直感的に回答できます。" },
    { id: "4",   position: "bottom", text: "全20問が終わったらレポートを送信" },
    { id: "5",   position: "bottom", text: "総合・カテゴリスコアや前月からの変化がダッシュボードに反映されます。" },
    { id: "6",   position: "bottom", text: "スコアと前月からの変化・注視項目をもとにフォロー対象の優先度が整理されます。" },
    { id: "7",   position: "bottom", text: "管理者へは声掛け・1on1・負荷調整などの推奨アクションを提示し、本人にはセルフケア・学習・キャリアのヒントを提示します。" },
    { id: "8",   position: "bottom", text: "実行したアクションの後、次月の変化で確認。点検で終わらない運用が自然に回ります。" },
    { id: "9",   position: "bottom", isLast: true,  text: "Signalyパルスサーベイの全機能を1ヵ月無料でお試しいただき、実際の操作感を体験してください。" }
  ] as const;

  // 2. map で focusClass を追加し、ここで初めて「完全な StepConfig[]」として型を確定させる
  const stepsData: StepConfig[] = rawSteps.map(step => ({ 
    ...step, 
    focusClass: `.focus-${step.id}` 
  }));

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      scrollTo: false,
      cancelIcon: { enabled: true },
      classes: "shepherd-theme-default",
    },
  });

  // 共通の画像切り替え関数
  const showStepImage = (stepId: string) => {
    document.querySelectorAll<HTMLElement>('.step-image').forEach(el => el.style.display = 'none');
    const stepNum = document.querySelector<HTMLElement>(`.step-${stepId}`);
    if (stepNum) stepNum.style.display = 'block';
  };

  // 表示状態のリセット
  const toggleTourView = (isTourActive: boolean) => {
    const stepArea = document.getElementById("step-area");
    const expDefault = document.querySelector<HTMLElement>(".exp-default");
    
    if (stepArea) stepArea.style.display = isTourActive ? "block" : "none";
    if (expDefault) expDefault.style.opacity = isTourActive ? "0" : "1";
    
    if (!isTourActive) {
      document.querySelectorAll<HTMLElement>('.step-image').forEach(el => el.style.display = 'none');
    }
  };

  // データに基づいてループでステップを追加
  stepsData.forEach((step) => {
    const buttons: any[] = [];

    if (step.isLast) {
      buttons.push(
        {
          text: "終了",
          action() {
            toggleTourView(false);
            tour.complete();
          },
        },
        {
          text: "無料体験を申込む",
          action() {
            window.location.href = "/forms/trial";
          },
        }
      );
    } else {
      if (!step.isFirst) {
        buttons.push({ text: "戻る", action: tour.back });
      }
      buttons.push({ text: "次へ", action: tour.next });
    }

    tour.addStep({
      id: `step-${step.id}`,
      text: step.text,
      attachTo: {
        element: step.focusClass,
        on: step.position,
      },
      beforeShowPromise() {
        return new Promise<void>((resolve) => {
          showStepImage(step.id);
          requestAnimationFrame(() => resolve());
        });
      },
      buttons,
    });
  });

  // ツアーイベントの監視
  tour.on('cancel', () => toggleTourView(false));

  startTour.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleTourView(true);
    tour.start();
  });
}